import { getNetwork } from '../../utils/getNetwork';
import { API_ROOT } from '../../config';
import { buildMeta, MOCK_STORE, clampDef, clampSur } from './mockData';

/* =====================================================================
 * schoolsApi.js — API модуля «Школы».
 *
 * Пока бэкенда нет — работаем на моке: USE_MOCK = true. Когда контроллер
 * появится, поставить USE_MOCK = false; адаптер (pick/def/tr) разрулит
 * контракт, как в остальных АРМ (вложенные {ru,kk}, camel/snake, обёртки).
 *
 * Базовый путь — под scadmin (как health): API_ROOT + '/api' + '/schools/...'.
 * Точные имена эндпоинтов и полей POST подтвердить у бэкендера, когда будет
 * Swagger (помечено ниже). Без ?? и ?. — react-app-rewired их не транспилирует.
 * ===================================================================== */

export const USE_MOCK = true;

const API_SCHOOLS_ROOT = API_ROOT + '/api'; // https://sc.smartkulsary.kz/api/scadmin/api
const META_ENDPOINT = '/schools/meta';
const RECORDS_ENDPOINT = '/schools/records';

export class ApiError extends Error {
  constructor(status, body) {
    super('api_error');
    this.status = status;
    this.body = body;
  }
}

/* --------------------------- helpers адаптера --------------------------- */

const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const pick = (o, key) => {
  if (o === null || o === undefined) return undefined;
  return o[key] !== undefined ? o[key] : o[toCamel(key)];
};

const def = (v, d) => (v === null || v === undefined ? d : v);

// nested {ru,kk} → строка; одноязычная строка → как есть; иначе плоские *_ru/*_kk.
const tr = (o, base, lang) => {
  if (o === null || o === undefined) return undefined;
  const nested = pick(o, base);
  if (nested !== null && typeof nested === 'object') return nested[lang];
  if (typeof nested === 'string') return nested;
  return pick(o, `${base}_${lang}`);
};

const bool = (v) => v === true || v === 'true' || v === 1 || v === '1';
const numOrNull = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// Строка справочника-школы → внутренняя модель.
function mapRow(r) {
  return {
    id: pick(r, 'id'),
    name: pick(r, 'name'),
    address: def(pick(r, 'address'), pick(r, 'addr')),
    capacity: numOrNull(pick(r, 'capacity')),
    shift: numOrNull(pick(r, 'shift')),
    lang_kz: bool(pick(r, 'lang_kz')),
    lang_ru: bool(pick(r, 'lang_ru')),
    students: numOrNull(pick(r, 'students')),
    teachers: numOrNull(pick(r, 'teachers')),
    deficit: numOrNull(pick(r, 'deficit')),
    surplus: numOrNull(pick(r, 'surplus')),
  };
}

function mapShift(s) {
  return {
    value: numOrNull(pick(s, 'value')),
    name_ru: tr(s, 'name', 'ru'),
    name_kk: tr(s, 'name', 'kk'),
  };
}

function mapLanguage(l) {
  return {
    code: pick(l, 'code'),
    name_ru: tr(l, 'name', 'ru'),
    name_kk: tr(l, 'name', 'kk'),
  };
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/* ------------------------------ Справочник ------------------------------ */
export async function fetchSchoolsMeta() {
  if (USE_MOCK) {
    await wait(120);
    return buildMeta();
  }
  const res = await getNetwork().get(API_SCHOOLS_ROOT + META_ENDPOINT);
  const data = res.data || {};
  const shifts = pick(data, 'shifts') || [];
  const languages = pick(data, 'languages') || [];
  return {
    shifts: shifts.map(mapShift),
    languages: languages.map(mapLanguage),
  };
}

/* ------------------------------- Перечень ------------------------------- */
export async function fetchSchoolsRows() {
  if (USE_MOCK) {
    await wait(150);
    return { rows: MOCK_STORE.rows.map(mapRow) };
  }
  const res = await getNetwork().get(API_SCHOOLS_ROOT + RECORDS_ENDPOINT);
  const data = res.data || {};
  const list =
    pick(data, 'items') || pick(data, 'rows') || pick(data, 'content') ||
    (Array.isArray(data) ? data : []);
  return { rows: list.map(mapRow) };
}

/* ------------------------------- Создание ------------------------------- */
// Тело POST: deficit/surplus НЕ отправляем (их считает сервер).
// Имена полей — предположение до появления Swagger; подтвердить у бэкендера.
function toBackendPayload(p) {
  return {
    name: p.name,
    address: p.address,
    shift: Number(p.shift),
    lang_kz: !!p.lang_kz,
    lang_ru: !!p.lang_ru,
    capacity: Number(p.capacity),
    students: Number(p.students),
    teachers: Number(p.teachers),
  };
}

export async function createSchool(payload) {
  const body = toBackendPayload(payload);

  if (USE_MOCK) {
    await wait(250);
    // Дубль наименования (409) — сравнение по имени без учёта регистра и краёв.
    const norm = (s) => String(s || '').trim().toLowerCase();
    const dup = MOCK_STORE.rows.some((r) => norm(r.name) === norm(body.name));
    if (dup) {
      throw new ApiError(409, {
        error:
          'duplicate_name: Школа с таким наименованием уже есть в перечне. Повтор недопустим. / Мұндай атаумен мектеп тізбеде бар. Қайталауға болмайды.',
      });
    }
    const row = {
      id: MOCK_STORE.nextId++,
      name: body.name,
      address: body.address,
      capacity: body.capacity,
      shift: body.shift,
      lang_kz: body.lang_kz,
      lang_ru: body.lang_ru,
      students: body.students,
      teachers: body.teachers,
      deficit: clampDef(body.students, body.capacity),
      surplus: clampSur(body.capacity, body.students),
    };
    MOCK_STORE.rows.unshift(row);
    return mapRow(row);
  }

  try {
    const res = await getNetwork().post(API_SCHOOLS_ROOT + RECORDS_ENDPOINT, body);
    if (res && (res.status === 201 || res.status === 200)) return mapRow(res.data || {});
    if (res) throw new ApiError(res.status, res.data);
    throw new ApiError(0, null);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError((e.response && e.response.status) || 0, e.response && e.response.data);
  }
}
